<template>
  <div>
    <ClientOnly>
      <div
        class="etx-container Container"
        :class="[isEditing ? 'etx-editing' : '', ...paragraphClassList]"
        v-if="teaserModuleDataSource?.data?.value?.length > 0 || isEditing"
      >
        <div class="etx-center Center">
          <!-- <LoaderSwiper /> -->
          <TeaserSliderComponent
            :dataSource="teaserModuleDataSource"
            :page-size="pageSize"
            :id="'references-swiper-1'"
            @selected="handleTeaserSelected($event)"
          />
        </div>
      </div>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import {
  LosysApiService,
  ReactiveAsyncDataSource,
  TeaserSliderComponent,
  type LosysApiAuthResult,
  type LosysApiProjectFilter,
  type TeaserCard,
} from '@etxdev/losys-lib'
import {
  getCompetenciesFromLosysIds,
  APPROVAL_BY_MARKETING_IDS,
  EQUANS_COMPANIES_IDS,
  KUMA_COMPANIES_IDS,
} from '~/composables/useCompetencyTaxonomy'

const tenantId = useTenant()
const { data: competencies } = await useAsyncData(
  `getCompetencyTermsLosys-${tenantId}`,
  async () => {
    const result = await useGraphqlQuery('getCompetencyTermsLosys', {
      vid: tenantId === 'equans' ? 'competencies' : 'competencies_kuma',
    })
    const items = result.data?.entityQuery?.items ?? []
    return items
  },
)
const currentLanguage = useCurrentLanguage().value || 'de'

const { options, isEditing } = defineBlokkli({
  bundle: 'references_swiper',
  globalOptions: ['spacingTop', 'spacingBottom'],
  editor: {
    addBehaviour: 'no-form',
    disableEdit: true,
    maxInstances: 1,
  },
})

const paragraphClassList = computed(() => {
  const classList = []

  if (options.value.spacingTop) {
    classList.push(options.value.spacingTop)
  }

  if (options.value.spacingBottom) {
    classList.push(options.value.spacingBottom)
  }

  return classList
})

/**
 * init losys api service
 * use etrex.dev proxy for now, which injects client secret into request
 * Setting the authStore param is not needed. Default behavior: token is stored within service instance.
 * If you want to store data elsewhere (as in example) pass a authStore impl.
 */
const apiService: LosysApiService = new LosysApiService({
  authEndpoint:
    'https://equans.etrex.live/middleware-losys/proxy.php?endpoint=auth&target=https://api.referenz-verwaltung.ch/oauth/token',
  apiHost: 'https://api.referenz-verwaltung.ch/api',
  authStore: {
    set: (data: LosysApiAuthResult) => {
      localStorage.setItem(
        'losys-auth',
        JSON.stringify({
          access_token: data.access_token,
          company: undefined!, // dont need it
        }),
      )
    },
    get: (): LosysApiAuthResult | undefined => {
      const storedData = localStorage.getItem('losys-auth')
      if (!storedData) return undefined

      return JSON.parse(storedData) as LosysApiAuthResult
    },
  },
})

if (import.meta.client) {
  apiService.authenticateAndGetBasicInformation().then(() => {
    loadData()
  })
}

const pageSize: number = 100 // default page size for data sources
const resultSize: number = 6 // Client side page size for results

/**
 * Teaser data source
 * Data source for the teaser list & slider component
 */

const contextualCompaniesIds =
  tenantId === 'kuma'
    ? KUMA_COMPANIES_IDS.map(String)
    : EQUANS_COMPANIES_IDS.map(String)

const teaserModuleDataSource: ReactiveAsyncDataSource<
  TeaserCard,
  LosysApiProjectFilter
> = new ReactiveAsyncDataSource<TeaserCard, LosysApiProjectFilter>(
  (filter: LosysApiProjectFilter): Promise<TeaserCard[]> => {
    return new Promise<TeaserCard[]>((resolve, _reject) => {
      // fetch data from api
      apiService
        .getCustomerProjects({
          ...filter,
          languages: [currentLanguage],
          expand: [
            'project_images',
            'project_categories',
            'project_properties',
          ],
          companyIds: contextualCompaniesIds,
        })
        .then((restData) => {
          // Filter by highlighted and approved projects
          const approvedData = restData
            .filter((y) => {
              const props = Object.values(y.project_properties)
              const isHighlight = props.find((y) =>
                IS_HIGHLIGHT_IDS.includes(y.projectAttributeId),
              )
              return isHighlight?.value !== 'True'
            })
            .filter((x) => {
              const props = Object.values(x.project_properties)
              const approvalByMarketing = props.find((x) =>
                APPROVAL_BY_MARKETING_IDS.includes(x.projectAttributeId),
              )
              return approvalByMarketing?.value === 'True'
            })
            // Remove duplicates results
            .filter(
              (item, index, self) => self.findIndex(i => i.id === item.id) === index
            )
            .sort(() => Math.random() - 0.5)
            .slice(0, resultSize)

          // map the retrieved data into teaser card data
          const data = approvedData.map((x) => {
            let imageUrl = (
              x.project_images && x.project_images?.length > 0
                ? x.project_images[0]?.large_image
                : ''
            ).replace(
              'https://api.referenz-verwaltung.ch/static/images/project/',
              'https://ik.imagekit.io/equans/losys/',
            )
            imageUrl = `${imageUrl}?tr=w-1000,ar-3-2,fo-auto`

            const projectCompetencies = getCompetenciesFromLosysIds(
              x.project_categories.map((x) => x.categoryId),
              true,
              competencies.value,
            ).map((x) => x.name)

            return {
              properties: {
                id: `${x.id}`,
                image: {
                  url: imageUrl,
                },
                tags: projectCompetencies,
                title: {
                  small: '', // x.zipcode, // will be "time to read"
                  main: x.title,
                  sub: x.city,
                },
              },
            }
          })
          resolve(data)
        })
    })
  },
)

/**
 * Triggers if teaser card was clicked within highlight module
 * @param card
 */
const handleTeaserSelected = (card: TeaserCard) => {
  window.location.href = `/${currentLanguage}/project/${card?.properties?.id}`
}

const loadData = () => {
  // initial load for teaser module data source
  teaserModuleDataSource.load(
    {
      pageIndex: 0,
      pageSize: pageSize,
      expand: ['project_images'],
    },
    'append',
  )
}
</script>
